import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { createSelector } from 'reselect';
import { forEach } from 'es-toolkit/compat';

import { CalendarParty } from 'types';
import { ServiceWeekdayMap } from 'state/services';
import { StoreType } from '@tock/shared/utils/connect';
import {
  TicketGroupsByDateTime,
  ticketGroupsByDateTimeSelector,
} from 'selectors/ticketGroupsByDateTimeSelector';
import { fastParseDate, fastParseDateTime, getBusinessDay } from 'utils/timeUtils';
import { getServiceEndTimeForDay, getServiceStartTimeForDay } from 'utils/CalendarRouteUtils';
import { serviceDateSelector } from 'selectors/service/serviceAppSelector';
import allCalendarPartiesSelector from 'selectors/allCalendarPartiesSelector';

interface DateTimeRange {
  startDateTime: LocalDateTime;
  endDateTime: LocalDateTime;
}

/**
 * This method is figuring out the first and last times, on the day of service,
 * that we should render in the calendar view.
 * The minimum range we show is the range of service hours for that day of the week, so
 * the start of lunch to the end of dinner.
 * However, if a party or ticket group exists outside of those times we still want to render it,
 * so we drag the range out to those times.
 *
 * If we are in the rare case that a walk-in was added on a different day (we no longer support this in the UI),
 * then that walk-in is ignored for the purposes of stretching the range.
 *
 * @param servicesForWeekday The service hours the business has configured, by weekday
 * @param parties The parties on this day. (bookings and walk-ins)
 * @param serviceDateString A string representation of the selected service date
 * @param ticketGroupsByDateTime All of the ticket groups for the given day
 * @returns The earliest time to render to the last time to render, on the given service date
 */
function calculateStartAndEndDateTime(
  servicesForWeekday: ServiceWeekdayMap,
  parties: CalendarParty[],
  serviceDateString: string | undefined,
  ticketGroupsByDateTime: TicketGroupsByDateTime
): DateTimeRange {
  // This should never happen but return something to make the types happy
  if (!serviceDateString) {
    return {
      startDateTime: LocalDate.now().atTime(LocalTime.of(4, 0)),
      endDateTime: LocalDate.now().atTime(LocalTime.of(4, 15)),
    };
  }
  const serviceDate = fastParseDate(serviceDateString);
  let startDateTime = getServiceStartTimeForDay(servicesForWeekday, serviceDate);
  let endDateTime = getServiceEndTimeForDay(servicesForWeekday, serviceDate);
  const dateTimesForPartiesAndTicketGroups: LocalDateTime[] = [];
  forEach(parties, (party) => {
    if (!party.dateTime15MinuteInterval) {
      return;
    }
    const startTime = fastParseDateTime(party.dateTime15MinuteInterval);
    dateTimesForPartiesAndTicketGroups.push(startTime);
    if (party.party && party.party.arrivalTime && party.party.leftTime) {
      // If there is a party that dined past the restaurant's close time, draw
      // extra boxes so it doesn't look weird.
      const partyTurnTimeSecs = party.party.leftTime - party.party.arrivalTime;
      const actualTurnTimeMins = Math.ceil(Math.round(partyTurnTimeSecs / 60) / 15) * 15;
      const leftTime = startTime.plusMinutes(actualTurnTimeMins);
      const bookingBusinessDay = getBusinessDay(startTime);
      const leftTimeBusinessDay = getBusinessDay(leftTime);
      // Super edge case: If the party left after 4AM, then only draw boxes
      // until 4AM. (Elsewhere we also truncate the party's box to 4AM so this
      // lines up.)
      if (leftTimeBusinessDay.isAfter(bookingBusinessDay)) {
        dateTimesForPartiesAndTicketGroups.push(
          bookingBusinessDay.plusDays(1).atTime(LocalTime.of(4, 0))
        );
      } else {
        dateTimesForPartiesAndTicketGroups.push(leftTime);
      }
    }
  });
  forEach(ticketGroupsByDateTime, (ticketGroups, ticketGroupDateTime) => {
    if (ticketGroups.some((ticketGroup) => ticketGroup.maxSize > 0)) {
      dateTimesForPartiesAndTicketGroups.push(fastParseDateTime(ticketGroupDateTime));
    }
  });

  forEach(dateTimesForPartiesAndTicketGroups, (dateTime) => {
    const businessDay = getBusinessDay(dateTime);
    // Tiny edge case: since end times live in `dateTimesForPartiesAndTicketGroups`
    // and an end time can be 4:00AM on the next day, we allow that here.
    if (
      !businessDay.isEqual(serviceDate) &&
      !dateTime.isEqual(serviceDate.plusDays(1).atTime(4, 0))
    ) {
      return;
    }
    if (dateTime.isBefore(startDateTime)) {
      startDateTime = dateTime;
    } else if (dateTime.isAfter(endDateTime)) {
      endDateTime = dateTime;
    }
  });
  return {
    startDateTime,
    endDateTime,
  };
}

export const startEndDateTimeSelector = createSelector(
  (state: StoreType) => state.services.forWeekday,
  allCalendarPartiesSelector,
  serviceDateSelector,
  ticketGroupsByDateTimeSelector,
  calculateStartAndEndDateTime
);

/**
 * This method produces an array of a LocalDateTime for every 15 minutes in between the earliest
 * and latest date times on the selected service date that we're going to render.
 *
 * @param dateTimeRange The earliest and latest dateTimes on the service date that we'll render in the calendar UI
 * @param serviceDateString A string representation of the selected service date
 * @returns A list of all the LocalDateTimes we should render
 */
function calculateDateTimesToRender(
  { startDateTime, endDateTime }: DateTimeRange,
  serviceDateString: string | undefined
): string[] {
  if (!serviceDateString) {
    return [];
  }
  const serviceDate = fastParseDate(serviceDateString);
  const dateTimes: string[] = [];
  const timeMin = serviceDate.atTime(LocalTime.of(4, 0));
  const timeMax = serviceDate.plusDays(1).atTime(LocalTime.of(3, 45));
  let timeCursor = !startDateTime.isBefore(timeMin) ? startDateTime : timeMin;

  while (timeCursor && !timeCursor.isAfter(endDateTime) && !timeCursor.isAfter(timeMax)) {
    dateTimes.push(timeCursor.toString());
    timeCursor = timeCursor.plusMinutes(15);
  }

  return dateTimes;
}

/** All the heavy lifting here is done by calculateDateTimesToRender */
export const dateTimesToRenderSelector = createSelector(
  startEndDateTimeSelector,
  serviceDateSelector,
  calculateDateTimesToRender
);

function computeFlexDateTimesToRender(
  serviceDate: string | undefined,
  dateTimesToRender: string[]
) {
  return {
    dateTimesToRender,
    serviceDate: serviceDate ? fastParseDate(serviceDate) : undefined,
  };
}

/**
 * This is a convenience selector for zipping up date times to render with a LocalDate of
 * the selected service date as well as the earliest start time we're going to render. This could
 * be broken up into multiple selectors at this point if we want. Previously, some of these
 * values fed into each other so it was good to have them in one place.
 */
export default createSelector(
  serviceDateSelector,
  dateTimesToRenderSelector,
  computeFlexDateTimesToRender
);
